import React, { useState } from 'react';
import _ from 'lodash';
import { useQuery, useMutation } from '@apollo/client';
import { Formik, Field } from 'formik';
import PropTypes from 'prop-types';
import { Form as FormSemantic, Checkbox } from 'semantic-ui-react';
import { ErrorHandler, InputFormField } from '@playbookux/playbook-web-core';
import {
  ButtonSave,
  ContainerButton,
  Text,
  MergeModal,
  MergeTagContainer,
  BorderContainer,
  ButtonCancel,
  ContainerColorPicker,
  ColorPicker,
} from './styles';

import schema from '../schemas/schemaModal';
import colorList from '../../../constants/color-list';

import {
  GET_TAGS_BY_COMPANY_ID,
  CREATE_MERGED_TAG_GROUP,
} from '../../../../core/graphql/tags';

const ModalMergeTagGroup = (props) => {
  const {
    companyId,
    defaultTagGroup,
    onClose,
    mergeTag,
    groupedTags,
    setMergeTag,
    refetch,
  } = props;

  const [errorMsg, setErrorMsg] = useState(null);
  const [tagsToBeMerged, setTagsToBeMerged] = useState([]);
  const [isChecked, setIsChecked] = useState(false);

  const {
    data,
  } = useQuery(GET_TAGS_BY_COMPANY_ID,
    {
      variables: {
        companyId,
      },
    });

  const [selectedColor, setSelectedColor] = useState(colorList[_.random(colorList.length - 1)]);

  const [createMergedTagGroup, { loadingMerge, error: hasErrorTag }] = useMutation(CREATE_MERGED_TAG_GROUP);

  const handleCloseModal = () => {
    setMergeTag({});
    onClose();
  };

  const handleMergeTagGroups = async (values, tags) => {
    setErrorMsg(null);
    const {
      name,
      description = '',
    } = values;
    tags.push(mergeTag.id);
    try {
      await createMergedTagGroup({
        variables: {
          name,
          description,
          color: selectedColor,
          companyId,
          tagGroupIds: tags,
        },
      });
      handleCloseModal();
      refetch();
    } catch (err) {
      const graphQLError = _.first(err.graphQLErrors);
      setErrorMsg(graphQLError);
    }
  };

  const handleCheck = (value, currentTagGroup) => {
    if (!value.checked) {
      setIsChecked(false);
      const newList = _.filter(tagsToBeMerged, (tag) => tag.id === currentTagGroup.id);
      setTagsToBeMerged(newList);
      return;
    }
    setTagsToBeMerged([...tagsToBeMerged, currentTagGroup.id]);
    setIsChecked(true);
  };

  const tagsCheckbox = () => groupedTags.map((groupedTag) => {
    if (groupedTag.id !== mergeTag.id) {
      return (
        <div>
          <MergeTagContainer>
            <Checkbox
              label={groupedTag.name}
              disabled={groupedTag.name === 'Ungrouped'}
              onChange={(event, value) => handleCheck(value, groupedTag)}
            />
            {' '}
          </MergeTagContainer>
        </div>
      );
    }
    return false;
  });

  const handleSelectColor = (color) => {
    setSelectedColor(color.hex);
  };

  return (
    <MergeModal
      open
      onClose={handleCloseModal}
      closeOnEscapeContainer
      closeOnDimmerClick
    >
      <Text as="h1">Merge Tag Groups</Text>
      <br />
      <Formik
        initialValues={{
          name: '',
          tagGroupKey: defaultTagGroup.key,
        }}
        validationSchema={schema}
        onSubmit={({ ...res }) => handleMergeTagGroups(res, tagsToBeMerged)}
      >
        {({
          errors,
          handleSubmit,
        }) => (
          <FormSemantic onSubmit={handleSubmit}>
            <FormSemantic.Field>
              <Text as="h3">{`Merge "${mergeTag.name}" with: `}</Text>
            </FormSemantic.Field>
            <BorderContainer>
              {tagsCheckbox(data)}
            </BorderContainer>
            <h3>Create a new tag group called:</h3>
            <Field
              name="name"
              error={errors.name}
              placeholder="Enter tag name"
              component={InputFormField}
            />
            <i>Note: this cannot be undone</i>
            <ContainerColorPicker>
              <Text as="h2">Color</Text>
              <ColorPicker
                width="300px"
                triangle="hide"
                colors={colorList}
                color={selectedColor}
                onChangeComplete={handleSelectColor}
              />
            </ContainerColorPicker>
            <ContainerButton>
              { errorMsg && (
                <ErrorHandler
                  error={errorMsg}
                  defaultMessage="Error while creating a tag"
                />
              )}
              <ButtonCancel onClick={onClose}>
                Cancel
              </ButtonCancel>
              <ButtonSave
                loading={loadingMerge}
                disabled={!isChecked}
                type="submit"
                error={hasErrorTag || errors.name}
              >
                Save
              </ButtonSave>
            </ContainerButton>
          </FormSemantic>
        )}
      </Formik>
    </MergeModal>
  );
};

ModalMergeTagGroup.propTypes = {
  defaultTagGroup: PropTypes.shape({
    id: PropTypes.string,
    key: PropTypes.string,
  }).isRequired,
  mergeTag: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
  }),
  groupedTags: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    color: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
  })).isRequired,
  onClose: PropTypes.func.isRequired,
  companyId: PropTypes.string.isRequired,
  setMergeTag: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
};

ModalMergeTagGroup.defaultProps = {
  mergeTag: {
    name: '',
    description: '',
  },
};
export default ModalMergeTagGroup;
